/**
 * The validator for an agency's report-branding custom properties, as used by
 * the agency workbench preview.
 *
 * MIRRORS tara-portal src/tenant/report-vars.ts. The portal injects the same
 * `<style data-tara-report-vars>` `:root` block into its report frame, and the
 * workbench preview has to render exactly what a client will see, so the rules
 * below (name pattern, value charset, length cap, url() ban) must stay
 * byte-for-byte in step with the portal copy. Change both or neither.
 *
 * `css_vars` is agency-authored data (the backend validates it as
 * `nullable|array` only, so both names and values are effectively arbitrary
 * strings) and here it reaches the report iframe as raw CSS TEXT inside an
 * injected `<style>` block (see buildReportVarsCss + src/lib/build-srcdoc.ts).
 * `<style>` is an HTML raw-text element, so its text content is serialized
 * UNESCAPED — a value carrying `</style`, `}` or `;` would escape the
 * declaration or the element itself.
 *
 * That is why the namespace guard alone is not enough and every value has to
 * clear a conservative allowlist as well.
 */

/** Only the report namespace may be written — never `--p-*` or a layout var. */
export const REPORT_VAR_RE = /^--r-[a-z0-9-]+$/;

/** Longest sane token value (a font stack is the worst case). */
export const MAX_VAR_VALUE = 200;

/**
 * Allowed value charset: colours (`#0A0A0A`, `rgb(10 10 10 / 50%)`), numbers
 * with units, keywords and font stacks (`'Mona Sans', system-ui, sans-serif`).
 *
 * Deliberately EXCLUDED, each one a way out of the declaration or the element:
 *   `;` `{` `}`  end the declaration / rule
 *   `<` `>`      end the `<style>` element (raw text, never escaped)
 *   `@`          start an at-rule
 *   `\`          CSS escape sequences (`\3c` is `<`)
 *   `:`          scheme in a URL, and pointless in a token value
 *   `*`          makes `/*` (and with it a comment) unconstructable
 *   `!`          `!important`
 *
 * The only whitespace allowed is the literal space — all a colour, a number, a
 * keyword or a font stack ever needs. `\s` used to admit a raw line break, CR,
 * U+2028, U+2029 or a non-breaking space ANYWHERE inside a value (trimming
 * only touches the ends). None of them can close the declaration or the
 * element today, so this is defence in depth: a value serialized into raw text
 * has no business carrying a line terminator.
 */
export const REPORT_VAR_VALUE_RE = /^[\w#%.,\-+/()'" ]+$/;

/** Belt and braces on top of the charset: no external fetch from a token. */
const URL_FUNCTION_RE = /url\s*\(/i;

export function isValidReportVarName(name: string): boolean {
  return REPORT_VAR_RE.test(name);
}

export function isValidReportVarValue(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= MAX_VAR_VALUE &&
    REPORT_VAR_VALUE_RE.test(value) &&
    !URL_FUNCTION_RE.test(value)
  );
}

/**
 * The validated `--r-*` entries, in payload order. Anything failing either
 * guard is dropped silently — a tenant token set is presentation data, and a
 * malformed entry must never take the preview (or the report) down.
 */
export function sanitizeReportVars(
  vars: Record<string, string> | null | undefined
): Array<[string, string]> {
  if (!vars || typeof vars !== 'object') return [];
  return Object.entries(vars).filter(
    ([name, value]) =>
      isValidReportVarName(name) && isValidReportVarValue(value)
  ) as Array<[string, string]>;
}

/**
 * The validated vars as a `:root` rule, or '' when an agency has none (the
 * unconfigured path — the caller then injects nothing at all).
 */
export function buildReportVarsCss(
  vars: Record<string, string> | null | undefined
): string {
  const entries = sanitizeReportVars(vars);
  if (entries.length === 0) return '';
  const body = entries
    .map(([name, value]) => `${name}:${value.trim()};`)
    .join('');
  return `:root{${body}}`;
}
